// ハザードマップポータル・空き家バンクへの外部リンク生成。
// UNVERIFIED: 重ねるハザードマップの ll/z/base クエリはページが読み込めることまでは確認したが、
// vs（表示レイヤー指定）の正確なエンコードは未確認のため付与していない（README・最終報告に記載）。
import type { Muni } from "../../schema/muni";
import { DEFAULT_LANG, t, type Lang } from "./i18n";
import { muniLabel } from "./prefNames";

export function disaportalDeepLink(muni: Muni, zoom = 13): string {
  const [lon, lat] = muni.lonlat;
  return `https://disaportal.gsi.go.jp/maps/index.html?ll=${lat},${lon}&z=${zoom}&base=pale`;
}

export interface LinkInfo {
  label: string;
  url: string;
}

const NATIONWIDE_AKIYA_BANKS: Record<Lang, { lifull: LinkInfo; athome: LinkInfo }> = {
  ja: {
    lifull: { label: "LIFULL HOME'S 空き家バンク", url: "https://www.homes.co.jp/akiyabank/" },
    athome: { label: "at home 空き家バンク", url: "https://www.akiya-athome.jp/" },
  },
  en: {
    lifull: { label: "LIFULL HOME'S akiya bank", url: "https://www.homes.co.jp/akiyabank/" },
    athome: { label: "at home akiya bank", url: "https://www.akiya-athome.jp/" },
  },
};

/**
 * GA4 の akiya_bank_click で区別する導線の種類。
 * local = 自治体公式ページ、athome = アットホーム空き家バンクの自治体専用サイト（物件一覧）、
 * search = 検索フォールバック、nationwide = 全国版サイトのトップ。
 */
export type AkiyaTarget = "local" | "athome" | "search" | "nationwide";

export interface AkiyaPrimaryLink extends LinkInfo {
  target: AkiyaTarget;
}

/** 検索クエリは日本語のまま組む（自治体のページは日本語でしか見つからない）。 */
export function akiyaSearchLink(muni: Muni, lang: Lang = DEFAULT_LANG): AkiyaPrimaryLink {
  const query = encodeURIComponent(`${muni.pref}${muni.name} 空き家バンク`);
  return {
    label: t(lang).akiyaSearch(muniLabel(muni.pref, muni.name, lang)),
    url: `https://www.google.com/search?q=${query}`,
    target: "search",
  };
}

/** http/https 以外（javascript: など）は自治体データの誤りとして扱い、検索リンクにフォールバックする。 */
function isSafeExternalUrl(url: string): boolean {
  return /^https:\/\//i.test(url) || /^http:\/\//i.test(url);
}

function safeUrl(url: string | null): string | null {
  return url && isSafeExternalUrl(url) ? url : null;
}

/** 主ボタンは 公式 > アットホーム自治体サイト > 検索。全国版の at home は自治体サイトがあればそちらに差し替える。 */
export function akiyaBankLinks(
  muni: Muni,
  lang: Lang = DEFAULT_LANG,
): { primary: AkiyaPrimaryLink; nationwide: LinkInfo[] } {
  const label = muniLabel(muni.pref, muni.name, lang);
  const official = safeUrl(muni.akiyaBankUrl);
  const athome = safeUrl(muni.akiyaAthomeUrl);
  const athomeLink: AkiyaPrimaryLink | null = athome
    ? { label: t(lang).akiyaAthome(label), url: athome, target: "athome" }
    : null;
  const primary: AkiyaPrimaryLink = official
    ? { label: t(lang).akiyaOfficial(label), url: official, target: "local" }
    : (athomeLink ?? akiyaSearchLink(muni, lang));
  const nationwide = NATIONWIDE_AKIYA_BANKS[lang];
  const athomeNationwide: LinkInfo =
    athomeLink && primary.target !== "athome"
      ? { label: t(lang).akiyaAthomeNationwide(muni.name), url: athomeLink.url }
      : nationwide.athome;
  return { primary, nationwide: [nationwide.lifull, athomeNationwide] };
}
